import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import configData from '../../config.json';

// Standardized wallet token key (§9.5) — the SAME key the stampWallet context and
// the Wallet page read. This is NOT `guest-uui` (the browse-gate timestamp).
const WALLET_TOKEN_KEY = 'hw-wallet-token';

// §6.5 / §9.5 — QR landing + guest bootstrap.
//
// A walk-in scans the venue/campaign QR and lands here at `/q/:token`. If the
// browser already holds a signed wallet token we DON'T mint a new wallet (idempotent,
// one wallet per device). Otherwise we POST /guest/wallet/init with the scanned token,
// store the signed `walletToken` under `hw-wallet-token`, and route into the wallet so
// the guest can start collecting. Fail-safe: any error shows a friendly message and a
// retry, never a white screen — and a scan NEVER mints a stamp by itself (A5); init
// only mints the wallet identity (A4, signed token).
export default function QrLanding() {
  const { token } = useParams();
  const navigate = useNavigate();

  const [error, setError] = useState(false);

  const bootstrap = async () => {
    setError(false);

    // A LOGGED-IN customer scanning the venue QR already has a wallet identity —
    // their account. Minting a guest wallet here would shadow it (the stampWallet
    // context prefers `hw-wallet-token`), so route straight into their wallet.
    try {
      const profile = JSON.parse(localStorage.getItem('user_profile') || 'null');
      if (profile?.token) {
        navigate('/wallet', { replace: true });
        return;
      }
    } catch (e) {
      // malformed profile → treat as anonymous
    }

    // Already have a wallet on this device → just route into it (idempotent).
    let existing = null;
    try {
      existing = localStorage.getItem(WALLET_TOKEN_KEY);
    } catch (e) {
      existing = null;
    }
    if (existing) {
      navigate('/wallet', { replace: true });
      return;
    }

    try {
      // The scanned `:token` is the venue/campaign scan token; the backend resolves
      // the restaurant from it. Identity is established from a signed token only (A4).
      const response = await axios.post(
        `${configData.SERVER_URL}/guest/wallet/init`,
        { token, deviceFp: navigator.userAgent || '' },
      );

      const walletToken = response?.data?.walletToken;
      if (!walletToken) {
        // No signed token back → can't safely bootstrap; show the friendly fallback.
        setError(true);
        return;
      }

      try {
        localStorage.setItem(WALLET_TOKEN_KEY, walletToken);
      } catch (e) {
        // Storage blocked (private mode / quota) → fail-safe to the message.
        setError(true);
        return;
      }

      // Route into the wallet/menu so the walk-in can start collecting. If init told
      // us which venue this is, drop them straight onto that restaurant's menu.
      const restaurant = response?.data?.restaurant;
      if (restaurant) {
        navigate(`/restaurants/${restaurant}`, { replace: true });
      } else {
        navigate('/wallet', { replace: true });
      }
    } catch (e) {
      // Network / rate-limit / invalid-or-expired QR → friendly, retryable message.
      console.log('Guest wallet init:', e.message);
      setError(true);
    }
  };

  useEffect(() => {
    bootstrap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (error) {
    return (
      <div
        style={{
          minHeight: '60vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          textAlign: 'center',
          padding: '24px',
        }}
      >
        <h3 style={{ marginBottom: 8 }}>We couldn't open your stamp card</h3>
        <p style={{ marginBottom: 16, maxWidth: 420 }}>
          The QR code may have expired or the connection dropped. Please ask a member of staff
          to show the code again, or try once more.
        </p>
        <button
          type="button"
          onClick={bootstrap}
          style={{
            border: 'none',
            borderRadius: 8,
            padding: '10px 20px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Try again
        </button>
      </div>
    );
  }

  // Bootstrapping in flight — minimal, branded-neutral placeholder.
  return (
    <div
      style={{
        minHeight: '60vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: '24px',
      }}
    >
      <p>Setting up your stamp card…</p>
    </div>
  );
}
